// store/orders/actions.ts
import makeRequest from "@/functions/http/makeRequest";
import { OrdersState, RESET_CART_ITEMS } from "./index";
import { storePedidoItem, pedidosItem, PedidosData, PedidoSelectedFilters } from "@/interfaces/Pedidos";
import { Commit } from "vuex";
import prices from "@/functions/prices";
import router from "@/router";

type ActionCtx = { commit: Commit, state?: OrdersState, dispatch: (action: string, payload?: any) => Promise<any> };

const actions = {
    resetCartItems({ commit }: ActionCtx) {
        commit(RESET_CART_ITEMS);
    },

    async removeOneFromCart(
        { commit, dispatch }: ActionCtx,
        {
            index,
            product,
            apiRoute,
        }: { index: number; product: pedidosItem; apiRoute: string }
    ) {
        const token = localStorage.getItem("authToken");
        const headers = { Authorization: `Bearer ${token}` };
        const data = { id: product.id, item: product.item };

        await makeRequest(
            apiRoute + "/pedido/destroyOnePedren",
            data,
            { headers },
            "DELETE"
        );
        commit("removeOneFromCart", index);
        // Aquí haces fetch de los nuevos datos
        // Si usas modules, puede que tengas que poner: dispatch("orders/fetchCartItems", ...)
        await dispatch("fetchCartItems", { apiRoute });
    },

    async removeFromCart(
        { commit }: ActionCtx,
        {
            index,
            order,
            apiRoute,
        }: { index: number; order: pedidosItem; apiRoute: string }
    ) {
        const token = localStorage.getItem("authToken");
        const headers = { Authorization: `Bearer ${token}` };
        const data = { id: order.id };

        await makeRequest(
            apiRoute + "/pedido/destroy",
            data,
            { headers },
            "DELETE"
        );

        commit("removeFromCart", index);
    },

    startNewOrder({ commit }: ActionCtx) {
        commit("createNewOrder");
    },

    async addToCart(
        { commit }: ActionCtx,
        {
            apiRoute,
            data,
        }: { apiRoute: string; data: storePedidoItem }
    ) {
        try {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const response = await makeRequest(
                apiRoute + "/pedido/store",
                data,
                { headers },
                "POST"
            );

            commit("addToCart", data);
            console.log("Pedido added to cart:", response.data);
        } catch (error) {
            console.error("Error adding pedido to cart:", error);
        }
    },

    async updateCartItemQuantity(
        { commit, state }: ActionCtx,
        {
            product,
            quantity,
            apiRoute,
        }: {
            product: storePedidoItem;
            quantity: number;
            apiRoute: string;
        }
    ) {
        const existingCartItem = state!.cartItems.items.find(
            (item: pedidosItem) => item.codprod === product.codprod
        );

        if (existingCartItem) {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const data = {
                ...product,
                quantity: quantity,
            };

            const response = await makeRequest(
                apiRoute + "/pedido/store",
                data,
                { headers },
                "POST"
            );

            commit("updateItemQuantity", {
                codprod: data.codprod,
                quantity: data.quantity,
            });

            console.log("Quantity updated for item:", response.data);
        }
    },

    async setItemDiscountMarca(
        { commit, state }: ActionCtx,
        {
            marca,
            discount,
            apiRoute,
        }: { marca: string; discount: number; apiRoute: string }
    ) {
        const cartItemsToApplyDiscount = state!.cartItems.items.filter(
            (item: pedidosItem) => item.marca === marca
        );

        const token = localStorage.getItem('authToken');
        const headers = { Authorization: `Bearer ${token}` };

        const updatedItems = cartItemsToApplyDiscount.map((item: pedidosItem) => {
            item.discountMarca = discount;
            return {
                ...item,
                neto: prices.getPrecioNeto(item, undefined, true, true, true)
            }
        });

        await makeRequest(
            apiRoute + '/pedido/store',
            updatedItems,
            { headers },
            "POST"
        );

        commit("setItemDiscountMarca", { cartItemsToApplyDiscount, marca, discount });
    },

    async updateCartItemDiscount(
        { commit, state }: ActionCtx,
        {
            product,
            discount,
            apiRoute,
        }: {
            product: storePedidoItem;
            discount: number;
            apiRoute: string;
        }
    ) {
        const existingCartItem = state!.cartItems.items.find(
            (item: pedidosItem) => item.codprod === product.codprod
        );

        if (existingCartItem) {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const data = {
                ...product,
                discountItem: discount
            };

            await makeRequest(
                apiRoute + "/pedido/store",
                data,
                { headers },
                "POST"
            );

            commit("updateItemDiscount", {
                codprod: data.codprod,
                discount: data.discountItem,
            });
        }
    },

    async fetchCartItemsQuantity(
        { commit }: ActionCtx,
        { apiRoute }: { apiRoute: string }
    ) {
        try {
            const token = localStorage.getItem("authToken");

            if (!token) {
                localStorage.removeItem("vuex");
                // Si no hay token, redirigimos al login (tu path "/")
                router.push("/");
                throw new Error("Authentication token not found");
            }

            const headers = { Authorization: `Bearer ${token}` };
            const response = await makeRequest(
                `${apiRoute}/pedido/obtainQuantity`,
                null,
                { headers },
                "GET"
            );

            const responseData = response.data as { items: any[] };

            if (response.status === 200 && Array.isArray(responseData.items)) {
                commit("updateCartItemsQuantity", responseData.items);
            }

            return response.data;
        } catch (error: any) {
            const status = error?.response?.status ?? null;

            if (status === 401) {
                // 2. Limpieza de seguridad
                localStorage.removeItem("authToken");
                localStorage.removeItem("vuex");
                commit("updateCartItemsQuantity", []);

                // 3. Redirección al login
                // Como en tu router el login es path: "/", usamos ese destino
                router.push("/");
            }

            console.error("Error fetching cart items:", error);
            throw error;
        }
    },

    async fetchCartItems(
        { commit }: ActionCtx,
        { apiRoute, branch }: { apiRoute: string, branch: string }
    ) {
        try {
            const token = localStorage.getItem("authToken");
            if (!token) throw new Error("Authentication token not found");

            const headers = { Authorization: `Bearer ${token}` };

            const obj = { marca: branch };
            const response = await makeRequest(
                `${apiRoute}/pedido/show`,
                obj,
                { headers },
                "POST"
            );
            const responseData = response.data as { items: any[] };
            if (response.status === 200 && Array.isArray(responseData.items)) {
                const items = responseData;
                commit("updateCartItems", items);
            }
            return responseData.items;
        } catch (error) {
            console.error("Error fetching cart items:", error);
        }
    },

    async sendEmail(
        { commit }: ActionCtx,
        {
            email,
            cc,
            comment,
            data,
            apiRoute,
            unds,
            renglones,
            total,
            ahorro,
        }: {
            email: string;
            cc: string[];
            comment: string;
            data: PedidosData;
            apiRoute: string;
            unds: number;
            renglones: number;
            total: number;
            ahorro: number;
        }
    ) {
        try {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const emailData = {
                email,
                cc,
                comment,
                items: [...data],
                marca: data[0].marca,
                unds,
                renglones,
                total,
                ahorro: total - ahorro,
            };

            // IMPORTANTE: capturar respuesta
            const res = await makeRequest(
                apiRoute + "/pedido/sendEmail",
                emailData,
                { headers },
                "POST"
            );

            commit("removeFromCart", emailData.marca);

            // opcional: retornar respuesta para que la vista pueda validar
            return res;
        } catch (error) {
            console.log(error);

            // CRÍTICO: propagar el error a la vista (para que entre al .catch)
            throw error;
        }
    },

    async generateSuggested(
        { commit }: ActionCtx,
        { apiRoute, data }: { apiRoute: string; data: PedidoSelectedFilters }
    ) {
        const token = localStorage.getItem("authToken");

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await makeRequest(
                `${apiRoute}/pedido/sugerido`,
                { ...data },
                { headers },
                "POST"
            );

            if (response.status === 200) {
                const items = (response.data as { items: any[] }).items;
                commit("updateCartItemsQuantity", items);
            }
        } catch (error) {
            console.log(error);
        }
    },

    async updateCartItemCost(
        { commit, state }: ActionCtx,
        {
            product,
            cost,
            apiRoute,
        }: {
            product: storePedidoItem;
            cost: number;
            apiRoute: string;
        }
    ) {
        const existingCartItem = state!.cartItems.items.find(
            (item: pedidosItem) => item.codprod === product.codprod
        );

        if (existingCartItem) {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const data = {
                ...product,
                precio_neto: cost
            };

            // Si tu backend espera un update, puedes ajustar el endpoint y método
            await makeRequest(
                apiRoute + "/pedido/store",
                data,
                { headers },
                "POST"
            );

            commit("updateItemCost", {
                codprod: data.codprod,
                precio_neto: data.costo,
            });
        }
    },
    async updateCartItemsBulk(
        { commit, state }: ActionCtx,
        { apiRoute, items }: { apiRoute: string; items: any[] }
    ) {
        const token = localStorage.getItem("authToken");
        const headers = { Authorization: `Bearer ${token}` };

        // 1) Ejecuta UNA sola llamada al backend (tu backend ya acepta arrays en store)
        await makeRequest(
            apiRoute + "/pedido/store",
            items,
            { headers },
            "POST"
        );

        // 2) Actualiza store local (opcional pero recomendado)
        //    Si ya tienes mutations tipo updateItemQuantity/updateItemDiscount/updateItemCost,
        //    puedes iterar y aplicarlas aquí.
        items.forEach((it) => {
            if (typeof it.quantity !== "undefined") {
                commit("updateItemQuantity", { codprod: it.codprod, quantity: it.quantity });
            }
            if (typeof it.discountItem !== "undefined") {
                commit("updateItemDiscount", { codprod: it.codprod, discount: it.discountItem });
            }
            if (typeof it.precio_neto !== "undefined") {
                commit("updateItemCost", { codprod: it.codprod, precio_neto: it.costo });
            }
        });
    }

};

export default actions;
